import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "@/redux/store";
import { useSelector } from "react-redux";

import { getUserHistory } from "@/redux/profile/history/selectors";
import { getReserveGrunts } from "@/redux/profile/grunt/selectors";
import { fetchHistory } from "@/redux/profile/history/asyncActions";
import { resetCardsState } from "@/redux/profile/cards/slice";

import { HistoryTabs } from "./components/HistoryTabs";
import { HistoryContent } from "./components/HistoryContent";
import { Button } from "@/components";
import { useHistoryData } from "./hooks/useHistoryData";
import { useCurrentWidth } from "@/hooks";
import { MediaBreakPoints } from "@/resources/constants";
import { LogoutConfirmModal } from "../../components/LogoutConfirmModal";
import arrowIcon from "@/assets/icons/arrowCalendarLeft.svg";
import { IItem } from "@/redux/profile/history/types";
import { HistorySkeleton } from "./components/HistorySkeleton";
import styles from "./History.module.scss";

export type HistoryEntry =
  | { type: 'year'; year: number }
  | { type: 'date'; dateKey: string; items: IItem[] };

const ITEMS_PER_PAGE = 5;

export const History: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { items, isLoading } = useSelector(getUserHistory);
  const { gruntsAvailable } = useSelector(getReserveGrunts);
  const windowWidth = useCurrentWidth();
  const isMobile = windowWidth <= MediaBreakPoints.DESKTOP;

  const [hasGruntReserveState, setHasGruntReserveState] = useState(false);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'completed'>('upcoming');
  const [upcomingPage, setUpcomingPage] = useState(0);
  const [completedPage, setCompletedPage] = useState(0);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const { upcoming, completed } = useHistoryData(items);

  useEffect(() => {
    dispatch(resetCardsState());
    dispatch(fetchHistory());
  }, [dispatch]);

  useEffect(() => {
    const focusEffect = async () => {
      setHasGruntReserveState(gruntsAvailable);
    };
    focusEffect();
  }, [gruntsAvailable]);

  const handleNavigate = (route: string) => {
    navigate(`/profile/${route}`);
  };

  const handleBack = () => {
    navigate('/profile');
  };

  const handleTabChange = (tab: 'upcoming' | 'completed') => {
    setActiveTab(tab);
    if (tab === 'upcoming') {
      setUpcomingPage(0);
    } else {
      setCompletedPage(0);
    }
  };

  // Сортировка дат — как в мобильном приложении
  const sortedUpcomingDates = useMemo(() =>
    Object.keys(upcoming).sort((a, b) => {
      const dateA = new Date(upcoming[a]?.[0]?.dateForSort || '').getTime();
      const dateB = new Date(upcoming[b]?.[0]?.dateForSort || '').getTime();
      return dateA - dateB;
    }),
  [upcoming]);

  const sortedCompletedDates = useMemo(() =>
    Object.keys(completed).sort((a, b) => {
      const dateA = new Date(completed[a]?.[0]?.dateForSort || '').getTime();
      const dateB = new Date(completed[b]?.[0]?.dateForSort || '').getTime();
      return dateB - dateA;
    }),
  [completed]);

  // Пагинация
  const paginatedUpcomingDates = sortedUpcomingDates.slice(0, (upcomingPage + 1) * ITEMS_PER_PAGE);
  const hasMoreUpcoming = paginatedUpcomingDates.length < sortedUpcomingDates.length;

  const paginatedCompletedDates = sortedCompletedDates.slice(0, (completedPage + 1) * ITEMS_PER_PAGE);
  const hasMoreCompleted = paginatedCompletedDates.length < sortedCompletedDates.length;

  // Группировка по годам с разделителями — как в мобильном приложении
  const upcomingGrouped = useMemo((): HistoryEntry[] => {
    const result: HistoryEntry[] = [];
    let currentYear: number | null = null;

    paginatedUpcomingDates.forEach(dateKey => {
      const items = upcoming[dateKey];
      if (!items || !items[0]) return;

      const dateStr = items[0].dateForSort?.split('T')[0] || items[0].dateForSort?.split(' ')[0] || '';
      const year = new Date(dateStr).getFullYear();

      if (year !== currentYear && !isNaN(year)) {
        currentYear = year;
        result.push({ type: 'year', year });
      }
      result.push({ type: 'date', dateKey, items });
    });

    return result;
  }, [paginatedUpcomingDates, upcoming]);

  const completedGrouped = useMemo((): HistoryEntry[] => {
    const result: HistoryEntry[] = [];
    let currentYear: number | null = null;

    paginatedCompletedDates.forEach(dateKey => {
      const items = completed[dateKey];
      if (!items || !items[0]) return;

      const dateStr = items[0].dateForSort?.split('T')[0] || items[0].dateForSort?.split(' ')[0] || '';
      const year = new Date(dateStr).getFullYear();

      if (year !== currentYear && !isNaN(year)) {
        currentYear = year;
        result.push({ type: 'year', year });
      }
      result.push({ type: 'date', dateKey, items });
    });

    return result;
  }, [paginatedCompletedDates, completed]);

  if (isLoading) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.content}>
          {isMobile && (
            <div className={styles.headerMobile}>
              <button className={styles.logoutButton} onClick={handleBack}>
                <img src={arrowIcon} alt="back" />
              </button>
              <h2 className={styles.title}>
                История покупок
              </h2>
            </div>
          )}
          {!isMobile && (
            <h2 className={styles.title}>
              История покупок
            </h2>
          )}
        </div>
        <div className={styles.historyContent}>
          <HistorySkeleton />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.content}>
          {isMobile && (
            <div className={styles.headerMobile}>
              <button className={styles.logoutButton} onClick={handleBack}>
                <img src={arrowIcon} alt="back" />
              </button>
              <h2 className={styles.title}>
                История покупок
              </h2>
            </div>
          )}
          {!isMobile && (
            <h2 className={styles.title}>
              История покупок
            </h2>
          )}
          <HistoryTabs activeTab={activeTab} onTabChange={handleTabChange} />
        </div>

        <div className={styles.historyContent}>
          {activeTab === 'upcoming' ? (
            <HistoryContent
              entries={upcomingGrouped}
              hasMore={hasMoreUpcoming}
              onLoadMore={() => setUpcomingPage(prev => prev + 1)}
            />
          ) : (
            <HistoryContent
              entries={completedGrouped}
              hasMore={hasMoreCompleted}
              onLoadMore={() => setCompletedPage(prev => prev + 1)}
            />
          )}
        </div>

        {hasGruntReserveState && (
          <Button
            newStyles={styles.gruntButton}
            onClickFunc={() => handleNavigate('gruntReserve')}
          >
            <span>Доплата грунта</span>
          </Button>
        )}
      </div>

      <LogoutConfirmModal 
        isOpen={isLogoutModalOpen}
        setIsOpen={setIsLogoutModalOpen}
      />
    </>
  );
};
